/**
 * Monthly CoCM billing card — a collapsible per-month read-only record on the
 * patient billing tab. The header shows attested minutes, an on-read
 * billing-code ESTIMATE (D14 — authoritative codes live in dbt), status chips
 * (submitted, current, locked, not-yet-closed, assessment freshness), and a
 * copy affordance. Expanding a card reveals the month's saved clinical summary
 * and activity log. Authoring (summary edits, AI generation, submission) lives
 * entirely in the stepped billing workflow modal; the card offers Copy Report,
 * a "Bill this month" launcher for non-closed months, and a Reopen action for
 * closed months.
 *
 * @agentic connections
 */

import { startTransition, useEffect, useState } from "react";
import {
  ActionIcon,
  Badge,
  Button,
  Card,
  Collapse,
  Divider,
  Group,
  Loader,
  Menu,
  Paper,
  Stack,
  Table,
  Text,
  Tooltip,
} from "@mantine/core";
import {
  IconAlertTriangle,
  IconCalendar,
  IconChevronDown,
  IconChevronRight,
  IconCopy,
  IconDotsVertical,
  IconLock,
  IconLockOpen,
  IconReportMoney,
} from "@tabler/icons-react";
import { showErrorNotification } from "@benchmark/ui/utils";
import type { Observation } from "@medplum/fhirtypes";
import { formatBillingMonthLabel } from "@benchmark/core/resolvers";
import { formatCareTimeEntryRow } from "@benchmark/core/resolvers";
import { COCM_ASSESSMENT_FRESHNESS_WEEKS } from "@benchmark/core/constants";
import { BillingStatusChip, formatCompactDate } from "./BillingStatusChip";

/** On-read billing-code estimate for a month (never persisted; D14). @agentic connections */
export type MonthEstimate = {
  /** Windowed billable minutes (estimate) for the Copy Report header. */
  billableMinutes: number;
  packageDisplay: string;
  hasEligibleAssessment: boolean;
  latestAssessmentDate?: string;
  /**
   * Concern 6: `false` means no completed intake → not billable this month
   * (codes are empty regardless of minutes). `undefined` = not evaluated.
   */
  hasCompletedIntake?: boolean;
};

/** Normalized per-month data used to render a billing card. @agentic connections */
export type MonthCardData = {
  billingMonth: string;
  /** Attested minutes (fact) shown as the headline. */
  minutes: number;
  /** On-read code estimate (labeled non-authoritative). */
  estimate?: MonthEstimate;
  clinicalSummary?: string;
  submitted: boolean;
  /** When the month's bill was flagged submitted (Q2 — the "✓ date"). */
  submittedAt?: string;
  closed: boolean;
  isCurrent: boolean;
  contributingObservationIds?: string[];
  /** Attested raw minutes for the persisted snapshot on submit/save. */
  attestedMinutes: number;
  /**
   * A past (non-current) month with billable activity but no persisted
   * summary Observation — synthesized on-read because no automated
   * month-close job has run for it yet. Drives the "Not yet closed" badge.
   */
  pastUnclosed?: boolean;
};

/** Shows a red error notification. @agentic connections */
export const notifyError = (title: string, err: unknown): void => {
  showErrorNotification(
    err instanceof Error ? err.message : "Unexpected error",
    { title },
  );
};

type MonthlyBillingCardProps = {
  data: MonthCardData;
  onCopy: (data: MonthCardData) => void;
  onReopen: (data: MonthCardData) => void;
  /** Lazily load the month's contributing care-time entries for the log. */
  onLoadEntries: (data: MonthCardData) => Promise<Observation[]>;
  /**
   * Launch the stepped billing workflow modal for this month (D25). When
   * provided and the month is not closed, the card shows a "Bill this month"
   * button — covering the current month, prior unclosed months, and reopened
   * months so every editable month can be (re)submitted through the modal.
   */
  onStartWorkflow?: (data: MonthCardData) => void;
};

/**
 * Code chip. For open months it's an on-read estimate ("… (est.)"); once the
 * month is submitted/locked the code is settled, so drop the estimate label and
 * tooltip rather than implying the figure could still change.
 */
const CodeEstimateBadge = ({
  packageDisplay,
  final = false,
}: {
  packageDisplay?: string;
  final?: boolean;
}) => (
  <Tooltip
    label={
      final
        ? "Billing code for this submitted month"
        : "Estimated from the time logged so far — the final billing code is confirmed at month-end"
    }
    withArrow
  >
    <Badge variant="light" color={packageDisplay ? "purple" : "gray"} size="sm">
      {packageDisplay || "No bill"}
      {final ? "" : " (est.)"}
    </Badge>
  </Tooltip>
);

/**
 * Assessment freshness status for a month — rendered as a status chip in the
 * card header alongside the current/locked/not-yet-closed chips, so the
 * "Assessment required" warning reads at a glance without expanding the card.
 * Consent is shown once at the tab header, not repeated on every card.
 */
const AssessmentMeta = ({
  estimate,
  submitted,
}: {
  estimate: MonthEstimate;
  submitted: boolean;
}) => {
  // Once the month is submitted the bill is settled (assessment freshness is
  // captured in the packet), so hide it entirely to cut header clutter.
  if (submitted) return null;
  // No completed intake blocks billing outright (concern 6) — takes precedence
  // over the assessment status.
  if (estimate.hasCompletedIntake === false) {
    return (
      <Tooltip
        label="No completed intake — the patient is not billable until an intake is completed"
        withArrow
      >
        <Badge
          variant="light"
          color="red"
          size="sm"
          leftSection={<IconAlertTriangle size={12} />}
        >
          Intake required
        </Badge>
      </Tooltip>
    );
  }
  // A current assessment is reassuring context — muted subtitle, not a chip.
  if (estimate.hasEligibleAssessment) {
    const assessedOn = formatCompactDate(estimate.latestAssessmentDate);
    return (
      <Text size="xs" c="dimmed">
        Assessed{assessedOn ? ` ${assessedOn}` : ""}
      </Text>
    );
  }
  // A missing assessment blocks billing — keep it a prominent warning.
  return (
    <Tooltip
      label={`No eligible assessment within the last ${COCM_ASSESSMENT_FRESHNESS_WEEKS} weeks`}
      withArrow
    >
      <Badge
        variant="light"
        color="orange"
        size="sm"
        leftSection={<IconAlertTriangle size={12} />}
      >
        Assessment required
      </Badge>
    </Tooltip>
  );
};

type ClinicalSummaryPanelProps = {
  summary: string;
};

/**
 * The month's saved clinical summary as its own visually distinct panel —
 * reads as a self-contained unit within the card rather than blending into the
 * surrounding activity section. Authoring happens in the stepped billing
 * workflow modal; the card only displays the persisted narrative.
 */
const ClinicalSummaryPanel = ({ summary }: ClinicalSummaryPanelProps) => (
  <Paper withBorder radius="sm" p="sm" bg="var(--mantine-color-gray-0)">
    <Text size="sm" fw={600} tt="uppercase" c="dimmed" mb={4}>
      Clinical Summary
    </Text>
    <Text
      size="sm"
      c={summary ? undefined : "dimmed"}
      style={{ whiteSpace: "pre-wrap" }}
    >
      {summary || "No summary recorded"}
    </Text>
  </Paper>
);

type CardActionsMenuProps = {
  data: MonthCardData;
  /**
   * Whether `Copy report` is enabled. The month must be locked (`closed`, which
   * the EOM billing submission produces) AND carry a saved clinical summary
   * before its frozen report can be copied.
   */
  canCopy: boolean;
  /** Tooltip explaining why copy is disabled (shown only when `!canCopy`). */
  copyDisabledReason: string;
  onCopy: (data: MonthCardData) => void;
  onReopen: (data: MonthCardData) => void;
};

/**
 * Consolidated per-card actions — `Copy report` (gated until the month is locked
 * in via the EOM billing submission) and, for closed months, a Reopen action.
 * Authoring and submission live in the stepped billing workflow modal, so the
 * card exposes no inline write toggle.
 */
const CardActionsMenu = ({
  data,
  canCopy,
  copyDisabledReason,
  onCopy,
  onReopen,
}: CardActionsMenuProps) => (
  <Menu withArrow position="bottom-end" shadow="md">
    <Menu.Target>
      <ActionIcon
        variant="subtle"
        color="gray"
        size="sm"
        aria-label="Billing month actions"
        onClick={(e) => e.stopPropagation()}
      >
        <IconDotsVertical size={16} />
      </ActionIcon>
    </Menu.Target>
    <Menu.Dropdown onClick={(e) => e.stopPropagation()}>
      <Tooltip label={copyDisabledReason} withArrow disabled={canCopy}>
        <Menu.Item
          leftSection={<IconCopy size={14} />}
          onClick={() => onCopy(data)}
          disabled={!canCopy}
        >
          Copy report
        </Menu.Item>
      </Tooltip>
      {data.closed ? (
        <Menu.Item
          leftSection={<IconLockOpen size={14} />}
          color="orange"
          onClick={() => onReopen(data)}
        >
          Reopen
        </Menu.Item>
      ) : null}
    </Menu.Dropdown>
  </Menu>
);

const ActivityLog = ({
  entries,
  loading,
}: {
  entries: Observation[] | null;
  loading: boolean;
}) => {
  if (loading) {
    return (
      <Group gap="xs" py="xs">
        <Loader size="xs" />
        <Text size="sm" c="dimmed">
          Loading activity…
        </Text>
      </Group>
    );
  }
  if (!entries || entries.length === 0) {
    return (
      <Text size="sm" c="dimmed">
        No activity logged this month.
      </Text>
    );
  }
  return (
    <Table verticalSpacing="xs" fz="sm">
      <Table.Thead>
        <Table.Tr>
          <Table.Th>Date</Table.Th>
          <Table.Th>Activity</Table.Th>
          <Table.Th style={{ textAlign: "right" }}>Time</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {entries.map((obs, index) => {
          const row = formatCareTimeEntryRow(obs);
          return (
            <Table.Tr key={obs.id ?? index}>
              <Table.Td>{row.when}</Table.Td>
              <Table.Td>
                {row.activity}
                {row.nonBillable ? (
                  <Text span size="xs" c="dimmed" ml={6}>
                    (non-billable)
                  </Text>
                ) : null}
              </Table.Td>
              <Table.Td
                style={{ textAlign: "right" }}
                c={row.nonBillable ? "dimmed" : undefined}
              >
                {row.minutes}
              </Table.Td>
            </Table.Tr>
          );
        })}
      </Table.Tbody>
    </Table>
  );
};

/** Collapsible monthly billing card. @agentic connections */
export const MonthlyBillingCard = ({
  data,
  onCopy,
  onReopen,
  onLoadEntries,
  onStartWorkflow,
}: MonthlyBillingCardProps) => {
  const [opened, setOpened] = useState(data.isCurrent);
  const [entries, setEntries] = useState<Observation[] | null>(null);
  const [entriesLoading, setEntriesLoading] = useState(false);

  // The card is always read-only history: authoring (summary edits, AI
  // generation, submission) happens in the stepped billing workflow modal. For
  // a current/open month the summary is authored in the modal, so an inline
  // "No summary recorded" panel is pure noise — only show the summary panel
  // once a locked month carries a saved narrative.
  const showClinicalSummary =
    data.closed && Boolean(data.clinicalSummary?.trim());

  // Copy report gate: the month must be LOCKED (`closed`) AND carry a saved
  // clinical summary. `closed` — not `submitted` — is the lock signal, because
  // that is exactly what `loadEntries` keys the frozen contributing-observation
  // list on; a `submitted && !closed` month (legacy data) still loads live
  // care-time scans, so enabling copy there would serialize non-final minutes
  // into the "frozen" report. An open month — even one with a preliminary
  // summary — is likewise not yet final. The EOM billing submission closes the
  // month (submit ⇒ closed), so this stays enabled for the normal flow.
  const isLocked = data.closed;
  const hasSummary = Boolean(data.clinicalSummary?.trim());
  const copyEnabled = isLocked && hasSummary;
  const copyDisabledReason = !isLocked
    ? "Submit this month's billing before copying the report"
    : "Save the clinical summary before copying";

  // Load the activity log lazily on expand, and refetch when the month's
  // underlying data changes (new care time, reopen) so the log never shows
  // stale rows and a transient error retries on the next relevant change. The
  // signature — not `data`'s identity — drives the refetch; `cancelled` guards
  // out-of-order responses.
  const entriesSignature = data.closed
    ? `closed:${(data.contributingObservationIds ?? []).join(",")}`
    : `open:${data.attestedMinutes}:${data.minutes}`;
  useEffect(() => {
    if (!opened) return;
    let cancelled = false;
    startTransition(() => setEntriesLoading(true));
    void onLoadEntries(data)
      .then((loaded) => {
        if (!cancelled) startTransition(() => setEntries(loaded));
      })
      .catch(() => {
        if (!cancelled) startTransition(() => setEntries([]));
      })
      .finally(() => {
        if (!cancelled) setEntriesLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // `data` identity changes every render; `entriesSignature` + `opened`
    // capture exactly when a refetch is warranted.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, entriesSignature, onLoadEntries]);

  return (
    <Card withBorder radius="md" padding="md">
      <Group
        justify="space-between"
        align="center"
        wrap="nowrap"
        style={{ cursor: "pointer" }}
        onClick={() => setOpened((o) => !o)}
      >
        <Group gap="sm" wrap="nowrap" align="center" style={{ minWidth: 0 }}>
          <ActionIcon variant="subtle" color="gray" size="sm" aria-hidden>
            {opened ? (
              <IconChevronDown size={16} />
            ) : (
              <IconChevronRight size={16} />
            )}
          </ActionIcon>
          <IconCalendar size={16} color="var(--mantine-color-dimmed)" />
          {/* Title + subtitle: month with its single lifecycle chip on top,
              assessment freshness demoted to a muted subtitle beneath (hidden
              once submitted). One clear hierarchy instead of competing chips. */}
          <Stack gap={2} style={{ minWidth: 0 }}>
            <Group gap="xs" wrap="nowrap" align="center">
              <Text fw={700}>{formatBillingMonthLabel(data.billingMonth)}</Text>
              {/* A submitted month is also locked, so "Submitted" stands in for
                  both; "Locked" only shows for a month closed without a
                  submission. */}
              {data.submitted ? (
                <BillingStatusChip label="Submitted" date={data.submittedAt} />
              ) : data.closed ? (
                <Badge
                  size="sm"
                  variant="light"
                  color="gray"
                  leftSection={<IconLock size={12} />}
                >
                  Locked
                </Badge>
              ) : data.pastUnclosed ? (
                <Tooltip
                  label="No month-close has run for this month yet — figures are a live estimate"
                  withArrow
                >
                  <Badge size="sm" variant="light" color="yellow">
                    Not yet closed
                  </Badge>
                </Tooltip>
              ) : null}
            </Group>
            {data.estimate ? (
              <AssessmentMeta
                estimate={data.estimate}
                submitted={data.submitted}
              />
            ) : null}
          </Stack>
        </Group>
        <Group
          gap="md"
          wrap="nowrap"
          align="center"
          onClick={(e) => e.stopPropagation()}
        >
          <Text fw={700} size="lg" style={{ whiteSpace: "nowrap" }}>
            {data.minutes}{" "}
            <Text span size="sm" c="dimmed" fw={400}>
              min
            </Text>
          </Text>
          <CodeEstimateBadge
            packageDisplay={data.estimate?.packageDisplay}
            final={data.submitted || data.closed}
          />
          {onStartWorkflow && !data.closed ? (
            <Button
              size="compact-sm"
              variant="light"
              leftSection={<IconReportMoney size={14} />}
              onClick={() => onStartWorkflow(data)}
            >
              Bill this month
            </Button>
          ) : null}
          <CardActionsMenu
            data={data}
            // Copy serializes the FROZEN report, so it must stay disabled until
            // the month is locked (`closed`) — which the EOM billing submission
            // does (submit ⇒ closed). A saved preliminary summary on a still-open
            // (or submitted-but-not-closed) month is NOT enough — the figures
            // aren't frozen yet — and a locked month must still carry a summary
            // for the report to be meaningful.
            canCopy={copyEnabled}
            copyDisabledReason={copyDisabledReason}
            onCopy={onCopy}
            onReopen={onReopen}
          />
        </Group>
      </Group>

      <Collapse in={opened}>
        <Divider my="sm" />

        {showClinicalSummary ? (
          <>
            <ClinicalSummaryPanel summary={data.clinicalSummary ?? ""} />

            <Divider my="sm" />
          </>
        ) : null}

        <Text size="sm" fw={600} tt="uppercase" c="dimmed" mb={4}>
          Activity Log
        </Text>
        <ActivityLog entries={entries} loading={entriesLoading} />
      </Collapse>
    </Card>
  );
};
